import React, { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
// Params are provided from Rails via ReactOnRails props
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import ProgramCard from '../../components/coachLed/ProgramCard.jsx';
import CoachChip from '../../components/coachLed/CoachChip.jsx';

const PROGRAMS_QUERY = gql`
  query SprintPrograms {
    sprintPrograms {
      id
      title
      slug
      summary
      level
      price_cents
      featured
      hero_image_url
      goals
      duration_days
      avg_rating
      enrollment_count
      is_group
      status
      coachProfile { id name headline bio avatar_url verified rating review_count }
    }
  }
`;

export default function CoachProfileContainer(props) {
  const coachId = (props && (props.coach_id || props.coachId)) || null;
  const { data, loading, error } = useQuery(PROGRAMS_QUERY);
  const programs = data?.sprintPrograms || [];
  const coach = programs.find((p) => String(p.coachProfile?.id) === String(coachId))?.coachProfile || null;
  const coachPrograms = programs.filter((p) => String(p.coachProfile?.id) === String(coachId));

  useEffect(() => {
    try {
      if (coach && window.ahoy && typeof window.ahoy.track === 'function') {
        window.ahoy.track('coach_profile_view', { coach_id: coach.id, programs: coachPrograms.length });
      }
    } catch (_) {}
  }, [coach && coach.id, coachPrograms.length]);

  if (loading) return <SingleColumnTemplate><div className="rounded-2xl border border-border bg-card p-6">Loading...</div></SingleColumnTemplate>;
  if (error || !coach) return <SingleColumnTemplate><div className="rounded-2xl border border-border bg-card p-6">Coach not found</div></SingleColumnTemplate>;

  return (
    <SingleColumnTemplate title={coach.name} subtitle={coach.headline} variant="wide">
      <div className="rounded-2xl border border-border bg-card p-8">
        <CoachChip coach={coach} showBio={true} linkToProfile={false} />
        <div className="mt-3 text-sm text-muted-foreground">
          {coach.verified ? <span className="mr-3 inline-flex items-center gap-1">✓ Verified</span> : null}
          {typeof coach.rating === 'number' ? (
            <span className="inline-flex items-center gap-1">
              <span className="text-yellow-500">★</span>{Number(coach.rating).toFixed(1)} ({coach.review_count || 0})
            </span>
          ) : null}
        </div>
      </div>
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Programs by {coach.name}</h2>
        {coachPrograms.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground">No published programs yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {coachPrograms.map((p) => <ProgramCard key={p.id} program={p} coach={coach} />)}
          </div>
        )}
      </div>
      {/* Impact metrics */}
      {(() => {
        const studentsCoached = coachPrograms.reduce((n, p) => n + (p.enrollment_count || 0), 0);
        const avgRating =
          typeof coach.rating === 'number'
            ? Number(coach.rating)
            : (coachPrograms.filter((p) => typeof p.avg_rating === 'number').reduce((n, p) => n + p.avg_rating, 0) /
                Math.max(1, coachPrograms.filter((p) => typeof p.avg_rating === 'number').length));
        const activePrograms =
          coachPrograms.filter((p) => p.status === 'published').length || coachPrograms.length;
        return (
          <div className="mt-8">
            <h3 className="text-xl font-semibold mb-3">Impact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="text-3xl font-bold">{studentsCoached}</div>
                <div className="text-sm text-muted-foreground">Students Coached</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="text-3xl font-bold">
                  {isFinite(avgRating) && !isNaN(avgRating) ? avgRating.toFixed(1) : '—'}
                </div>
                <div className="text-sm text-muted-foreground">Average Rating</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="text-3xl font-bold">{activePrograms}</div>
                <div className="text-sm text-muted-foreground">Active Programs</div>
              </div>
            </div>
          </div>
        );
      })()}
    </SingleColumnTemplate>
  );
}


